import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import Dropdown from "../../components/Dropdown";
import InputText from "../../components/InputText";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import axiosInstance from "../../utils/axiosInstance";
import { calculateEqualPayment } from "../../utils/paymentCalculations/calculateEqualPayment";
import { calculateIncreasingPayment } from "../../utils/paymentCalculations/calculateIncreasingPayment";
import { calculateDecreasingPayment } from "../../utils/paymentCalculations/calculateDecreasingPayment";
import DynamicPayment from "../../utils/paymentCalculations/DynamicPayment";
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from "docx";
import { saveAs } from "file-saver";
import { useLoading } from "../../components/Loading/LoadingProvider";
import { useConfirmDialog } from "../../utils/ConfirmDialog/ConfirmDialogProvider";
import WarningArea from "../../components/WarningArea";

const AddPay = () => {
    const toast = useRef(null);
    const { setLoading } = useLoading();
    const { showDialog } = useConfirmDialog();

    const [customerList, setCustomerList] = useState([]);
    const [guarantorList, setGuarantorList] = useState([]);
    const [calculatedPayments, setCalculatedPayments] = useState({});
    const [showPaymentTable, setShowPaymentTable] = useState(false);
    const [showDynamicPaymentDialog, setShowDynamicPaymentDialog] = useState(false);

    const paymentTypes = [
        { id: 0, value: "Hepsi" },
        { id: 1, value: "Eşit Ödeme" },
        { id: 2, value: "Artan Ödeme" },
        { id: 3, value: "Azalan Ödeme" },
        { id: 4, value: "Dinamik Ödeme" }
    ];

    const accordionPT = {
        header: {
            className: "border rounded-md p-2 sm:p-3 transition-colors duration-300 " +
                "bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
        },
        headerAction: {
            className: "hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors duration-300"
        },
        headerIcon: {
            className: "text-gray-700 dark:text-gray-300"
        },
        headerTitle: {
            className: "font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100"
        },
        content: {
            className: "border border-t-0 rounded-b-md p-2 sm:p-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 transition-colors duration-300"
        }
    };

    const dataTablePT = {
        header: {
            className: "bg-gray-100 dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 font-semibold text-xs sm:text-sm"
        },
        bodyCell: {
            className: "bg-white dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-200 text-xs sm:text-sm p-1 sm:p-2"
        },
        paginator: {
            className: "bg-gray-100 dark:bg-gray-900 border-t border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 [&_button]:text-gray-900 dark:[&_button]:text-gray-100"
        }
    };

    const validationSchema = Yup.object().shape({
        customer: Yup.object().nullable().required("Müşteri seçimi zorunludur."),
        term: Yup.number()
            .required("Vade zorunludur.")
            .min(1, "Vade en az 1 ay olmalıdır.")
            .max(360, "Vade en fazla 360 ay olabilir.")
            .integer("Vade tam sayı olmalıdır."),
        amount: Yup.mixed()
            .required("Tutar zorunludur.")
            .test("is-valid-number", "Geçerli bir sayı giriniz.", (value) => {
                if (typeof value === "string") value = value.replace(",", ".");
                return !isNaN(value) && parseFloat(value) > 0;
            }),
        rate: Yup.mixed()
            .required("Faiz oranı zorunludur.")
            .test("is-valid-number", "Geçerli bir sayı giriniz.", (value) => {
                if (typeof value === "string") value = value.replace(",", ".");
                return !isNaN(value) && parseFloat(value) >= 0 && parseFloat(value) <= 100;
            }),
        firstPaymentDate: Yup.mixed()
            .test('is-date', "Hatalı Tarih Formatı", function (value) {
                if (!value) return false;
                if (value instanceof Date && !isNaN(value.getTime())) return true;
                return false;
            })
            .required("İlk Taksit Tarihi zorunludur."),
        remark: Yup.string().required("Açıklama zorunludur."),
        paymentType: Yup.number().nullable().required("Ödeme tipi seçimi zorunludur."),
        periodInput: Yup.mixed().when('paymentType', {
            is: (paymentType) => paymentType === 2 || paymentType === 3,
            then: () => Yup.number()
                .required("Periyot girişi zorunludur.")
                .min(1, "Periyot en az 1 olmalıdır.")
                .max(12, "Periyot en fazla 12 olmalıdır."),
            otherwise: () => Yup.mixed().nullable()
        })
    });

    // KVKK onayı kontrolü için yardımcı fonksiyon
    const checkKvkkApproval = (person, type) => {
        if (!person || !person.kvkk || person.kvkk.id === 0) {
            toast.current.show({
                severity: "warn",
                summary: "KVKK Onayı Gerekli",
                detail: `Seçilen ${type} KVKK onayı vermemiştir. İşleme devam edebilmek için ${type} gelen SMS'den KVKK onayı vermelidir.`,
                life: 5000,
            });
            return false;
        }
        return true;
    };

    const getCustomers = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/v1/customers');
            if (response?.data?.length === 0) {
                toast.current.show({
                    severity: "warn",
                    summary: "Uyarı",
                    detail: "Mevcut müşteri bulunamadı.",
                    life: 3000,
                });
            } else {
                setCustomerList(response?.data?.filter((item) => item.status.id === 3));
            }
        } catch (error) {
            console.error(error)
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Müşteri verisi getirilirken bir hata oluştu.",
                life: 3000,
            });
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const getGuarantors = async (customerId) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/v1/guarantors/${customerId}`);
            if (response?.data?.length === 0) {
                toast.current.show({
                    severity: "warn",
                    summary: "Uyarı",
                    detail: "Müşteri için kefil bilgisi mevcut değildir.",
                    life: 3000,
                });
            } else {
                setGuarantorList(response?.data?.filter((item) => item.status.id === 3));
            }
        } catch (error) {
            console.error(error)
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Kefil verisi getirilirken bir hata oluştu.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            customer: null,
            guarantor: null,
            term: "",
            amount: "",
            rate: "0.00", // Default olarak 0.00
            firstPaymentDate: new Date(), // Default olarak bugün
            remark: "",
            paymentType: 0,
            periodInput: 3,
            paymentDetailList: []
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            setLoading(true);
            try {
                if (values.paymentType === 0) {
                    toast.current.show({
                        severity: "error",
                        summary: "Hata",
                        detail: "Lütfen bir ödeme tipi seçiniz.",
                        life: 3000,
                    });
                    setSubmitting(false);
                    return;
                }

                // Kaydetme öncesi KVKK kontrolleri
                if (!checkKvkkApproval(values.customer, "müşteri")) {
                    setSubmitting(false);
                    return;
                }

                if (values.guarantor && !checkKvkkApproval(values.guarantor, "kefil")) {
                    setSubmitting(false);
                    return;
                }

                const amount = parseFloat(values.amount.toString().replace(/,/g, "."));
                const rate = parseFloat(values.rate.toString().replace(/,/g, "."));

                let paymentDetailList = values.paymentDetailList;
                if (!paymentDetailList || paymentDetailList.length === 0) {
                    const calculationData = {
                        orderAmount: amount,
                        term: parseInt(values.term),
                        rate: rate,
                        periodInput: parseInt(values.periodInput || 0)
                    };

                    switch (values.paymentType) {
                        case 1:
                            paymentDetailList = calculateEqualPayment(calculationData);
                            break;
                        case 2:
                            paymentDetailList = calculateIncreasingPayment(calculationData);
                            break;
                        case 3:
                            paymentDetailList = calculateDecreasingPayment(calculationData);
                            break;
                        case 4:
                            toast.current.show({
                                severity: "error",
                                summary: "Hata",
                                detail: "Dinamik ödeme planı oluşturulmamış.",
                                life: 3000,
                            });
                            setSubmitting(false);
                            return;
                        default:
                            paymentDetailList = [];
                    }

                    if (paymentDetailList.length === 0) {
                        toast.current.show({
                            severity: "error",
                            summary: "Hata",
                            detail: "Ödeme planı oluşturulamadı.",
                            life: 3000,
                        });
                        setSubmitting(false);
                        return;
                    }
                }
                await axiosInstance.post('/api/v1/payment/create', {
                    customerId: values.customer.id,
                    guarantorId: values?.guarantor?.id,
                    term: values.term,
                    amount: amount,
                    rate: rate,
                    firstPaymentDate: values?.firstPaymentDate ? values?.firstPaymentDate.toISOString() : null,
                    remark: values.remark,
                    paymentType: values.paymentType,
                    period: values.periodInput,
                    paymentDetailList: paymentDetailList
                });

                toast.current.show({
                    severity: "success",
                    summary: "Başarılı",
                    detail: "Ödeme başarılı bir şekilde eklendi.",
                    life: 3000,
                });

                resetForm();
                // Reset sonrası default değerleri tekrar set et
                formik.setFieldValue("rate", "0.00");
                formik.setFieldValue("firstPaymentDate", new Date());
                setCalculatedPayments({});
                setShowPaymentTable(false);
                setShowDynamicPaymentDialog(false);

            } catch (error) {
                console.error(error)
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: "Ödeme eklenirken bir hata oluştu.",
                    life: 3000,
                });
            } finally {
                setSubmitting(false);
                setTimeout(() => {
                    setLoading(false);
                }, 1 * 1000);
            }
        }
    });

    const generateDOCX = () => {
        // Türkçe tarih formatı için yardımcı fonksiyon
        const formatTurkishDate = (date) => {
            const months = [
                'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
            ];

            const day = date.getDate();
            const month = months[date.getMonth()];
            const year = date.getFullYear();

            return `${day} ${month} ${year}`;
        };

        // Ödeme tarihi hesaplama fonksiyonu
        const getPaymentDateForWord = (monthIndex) => {
            if (!formik.values.firstPaymentDate) return monthIndex.toString();

            const baseDate = new Date(formik.values.firstPaymentDate);
            baseDate.setMonth(baseDate.getMonth() + monthIndex - 1);
            return formatTurkishDate(baseDate);
        };

        const date = formatTurkishDate(new Date());
        const doc = new Document({
            sections: Object.entries(calculatedPayments).map(([paymentType, payments]) => {
                const amount = formik.values.amount ? parseFloat(formik.values.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : "";
                const term = formik.values.term || "";
                const rate = formik.values.rate ? parseFloat(formik.values.rate).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : "";
                const period = formik.values.periodInput || "";
                const totalPayback = payments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
                const totalInterest = payments.reduce((sum, payment) => sum + parseFloat(payment.interest), 0);

                return {
                    properties: {},
                    children: [
                        new Paragraph({
                            text: `Ödeme Planı Raporu - ${date}`,
                            heading: "Title",
                            spacing: { after: 300 }
                        }),
                        new Paragraph({
                            text: `${paymentType} Planı`,
                            heading: "Heading1",
                            spacing: { after: 200 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: `Ana Para: `, bold: true }),
                                new TextRun(`₺${amount}`)
                            ],
                            spacing: { after: 100 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: `Vade (Ay): `, bold: true }),
                                new TextRun(term.toString())
                            ],
                            spacing: { after: 100 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: `Periyot (Ay): `, bold: true }),
                                new TextRun(period.toString())
                            ],
                            spacing: { after: 100 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: `İlk Ödeme Tarihi: `, bold: true }),
                                new TextRun(formik.values.firstPaymentDate ? formatTurkishDate(formik.values.firstPaymentDate) : "-")
                            ],
                            spacing: { after: 100 }
                        }),
                        // Yeni eklenen toplam bilgiler
                        new Paragraph({
                            children: [
                                new TextRun({ text: `Toplam Geri Ödeme: `, bold: true }),
                                new TextRun(`₺${totalPayback.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)
                            ],
                            spacing: { after: 100 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({ text: `Toplam Faiz: `, bold: true }),
                                new TextRun(`₺${totalInterest.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)
                            ],
                            spacing: { after: 200 }
                        }),
                        new Table({
                            width: {
                                size: 100,
                                type: WidthType.PERCENTAGE,
                            },
                            rows: [
                                new TableRow({
                                    children: [
                                        new TableCell({ children: [new Paragraph(formik.values.firstPaymentDate ? "Vade Tarihi" : "Ay")] }),
                                        new TableCell({ children: [new Paragraph("Taksit Tutarı")] }),
                                        new TableCell({ children: [new Paragraph("Anapara")] }),
                                        new TableCell({ children: [new Paragraph("Faiz")] }),
                                        new TableCell({ children: [new Paragraph("Kalan Anapara")] }),
                                    ],
                                }),
                                ...payments.map(payment =>
                                    new TableRow({
                                        children: [
                                            new TableCell({ children: [new Paragraph(formik.values.firstPaymentDate ? getPaymentDateForWord(payment.month) : payment.month.toString())] }),
                                            new TableCell({ children: [new Paragraph(`₺${parseFloat(payment.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)] }),
                                            new TableCell({ children: [new Paragraph(`₺${parseFloat(payment.principal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)] }),
                                            new TableCell({ children: [new Paragraph(`₺${parseFloat(payment.interest).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)] }),
                                            new TableCell({ children: [new Paragraph(`₺${parseFloat(payment.remainingPrincipal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)] }),
                                        ],
                                    })
                                ),
                                new TableRow({
                                    children: [
                                        new TableCell({ children: [new Paragraph({ text: "TOPLAM", bold: true })] }),
                                        new TableCell({ children: [new Paragraph({ text: `₺${totalPayback.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`, bold: true })] }),
                                        new TableCell({ children: [new Paragraph({ text: `₺${amount}`, bold: true })] }),
                                        new TableCell({ children: [new Paragraph({ text: `₺${totalInterest.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`, bold: true })] }),
                                        new TableCell({ children: [new Paragraph({ text: "-", bold: true })] }),
                                    ],
                                })
                            ]
                        })
                    ]
                };
            })
        });

        Packer.toBlob(doc).then(blob => {
            saveAs(blob, `odeme-plani-${date.replace(/\s/g, '-')}.docx`);
        }).catch(err => {
            console.error("DOCX oluşturulurken hata:", err);
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Word dosyası oluşturulurken bir hata oluştu.",
                life: 3000,
            });
        });
    };

    useEffect(() => {
        const paymentType = formik.values.paymentType;
        if (paymentType === 0 || paymentType === 2 || paymentType === 3) {
            formik.setFieldValue("periodInput", 3);
        } else {
            formik.setFieldValue("periodInput", 0);
        }

        setShowPaymentTable(false);
        formik.setFieldValue("paymentDetailList", []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.paymentType]);

    const handlePaymentCalculation = useCallback(() => {
        const { paymentType, amount, term, rate, periodInput } = formik.values;

        if (!amount || !term || rate === null || rate === undefined || paymentType === null) {
            toast.current.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "Lütfen gerekli alanları doldurunuz.",
                life: 3000,
            });
            return;
        }

        if ((paymentType === 2 || paymentType === 3) && !periodInput) {
            toast.current.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "Lütfen periyot değerini giriniz.",
                life: 3000,
            });
            return;
        }
        setLoading(true);

        try {
            const amountValue = parseFloat(amount.toString().replace(/,/g, "."));
            const rateValue = parseFloat(rate.toString().replace(/,/g, "."));

            const calculationData = {
                orderAmount: amountValue,
                term: parseInt(term),
                rate: rateValue,
                periodInput: parseInt(periodInput || 0)
            };

            if (paymentType === 4) {
                setShowDynamicPaymentDialog(true);
                return;
            }

            let allPayments = {};
            let currentPayPlan = [];

            if (paymentType === 0) {
                allPayments = {
                    "Eşit Ödeme": calculateEqualPayment(calculationData),
                    "Artan Ödeme": calculateIncreasingPayment(calculationData),
                    "Azalan Ödeme": calculateDecreasingPayment(calculationData)
                };
                currentPayPlan = allPayments["Eşit Ödeme"];
            } else {
                let paymentTypeName = paymentTypes.find(t => t.id === paymentType)?.value || "";
                let calculatedPayment;

                switch (paymentType) {
                    case 1:
                        calculatedPayment = calculateEqualPayment(calculationData);
                        break;
                    case 2:
                        calculatedPayment = calculateIncreasingPayment(calculationData);
                        break;
                    case 3:
                        calculatedPayment = calculateDecreasingPayment(calculationData);
                        break;
                    default:
                        calculatedPayment = [];
                }

                allPayments = {
                    [paymentTypeName]: calculatedPayment
                };
                currentPayPlan = calculatedPayment;
            }

            setCalculatedPayments(allPayments);
            formik.setFieldValue("paymentDetailList", currentPayPlan);
            setShowPaymentTable(true);
        } catch (error) {
            console.error(error);
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Ödeme planı hesaplanırken bir hata oluştu.",
                life: 3000,
            });
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik, paymentTypes, toast]);

    const getPaymentDate = (monthIndex) => {
        if (!formik.values.firstPaymentDate) return null;

        const baseDate = new Date(formik.values.firstPaymentDate);
        baseDate.setMonth(baseDate.getMonth() + monthIndex);
        return baseDate;
    };

    const formatPaymentDate = (date) => {
        if (!date) return null;
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleDynamicPaymentGenerate = (payments) => {
        setCalculatedPayments({
            "Dinamik Ödeme": payments
        });
        formik.setFieldValue("paymentDetailList", payments);
        setShowPaymentTable(true);
    };

    useEffect(() => {
        getCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="w-full p-3 max-h-screen overflow-y-auto sm:mb-20 sm:mt-20">
            <Toast ref={toast} />
            <div className="flex justify-end sm:mb-4">
                <p className="text-red-600 dark:text-red-400 font-semibold italic text-xs md:text-sm">
                    * Ödeme kaydetmek için zorunlu alanları ifade eder.
                </p>
            </div>
            <div className="flex justify-end sm:mb-4">
                <p className="text-red-600 dark:text-red-400 font-semibold italic text-xs md:text-sm">
                    ** Ödeme listesi örneği görmek için tutar, faiz oranı ve vade bilgisi girmeniz yeterlidir.
                </p>
            </div>
            <div className="flex flex-col gap-4 my-6">
                <WarningArea type='info' message="Ödeme planının oluşturulabilmesi için; vade, tutar ve faiz bilgileri eksiksiz ve doğru şekilde girilmelidir." />
                <WarningArea type='info' message="Periyot bilgisi, artan ve azalan ödeme seçeneklerinde zorunlu bir alandır. Bu alan, ödemelerin hangi periyotlarla artacağı veya azalacağı bilgisini ifade eder." />
            </div>
            <form className="flex-col space-y-4 sm:space-y-6 text-gray-900 dark:text-gray-100">
                {/* Form Grid - Responsive */}
                <div className="mt-3 sm:mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-10">
                    <Dropdown
                        id="paymanetType"
                        name="paymentType"
                        value={formik.values.paymentType}
                        options={paymentTypes}
                        optionLabel="value"
                        optionValue="id"
                        onChange={(e) => {
                            formik.setFieldValue("paymentType", e.value)
                        }}
                        label="Ödeme Tipi"
                        error={formik.errors.paymentType}
                        touched={formik.touched.paymentType}
                        required
                        onBlur={formik.handleBlur}
                    />
                    <Dropdown
                        id="customer"
                        name="customer"
                        value={formik.values.customer}
                        options={customerList}
                        optionLabel="customerName"
                        onChange={(e) => {
                            if (e.value && !checkKvkkApproval(e.value, "müşteri")) {
                                return;
                            }
                            formik.setFieldValue("customer", e.value);
                            setGuarantorList([])
                            formik.setFieldValue("guarantor", null)
                            if (e?.value?.id) { getGuarantors(e.value.id) }
                        }}
                        onBlur={formik.handleBlur}
                        label="Müşteri"
                        showClear
                        error={formik.errors.customer}
                        touched={formik.touched.customer}
                        required
                        valueTemplate={(option, props) => {
                            if (option) {
                                return (
                                    <div className="flex items-center gap-2">
                                        <span>{option.name} {option.surname}</span>
                                        {option.kvkk && option.kvkk.id === 0 && (
                                            <i className="pi pi-exclamation-triangle text-orange-500" title="KVKK onayı yok"></i>
                                        )}
                                    </div>
                                );
                            }
                        }}
                        itemTemplate={(option) => {
                            return (
                                <div className="flex items-center gap-2">
                                    <span>{option.name} {option.surname}</span>
                                    {option.kvkk && option.kvkk.id === 0 && (
                                        <i className="pi pi-exclamation-triangle text-orange-500" title="KVKK onayı yok"></i>
                                    )}
                                </div>
                            );
                        }}
                        filter
                        filterBy="name,surname,identityNumber,phoneNumber1"
                    />
                    <Dropdown
                        id="guarantor"
                        name="guarantor"
                        value={formik.values.guarantor}
                        options={guarantorList}
                        optionLabel="name"
                        onChange={(e) => {
                            if (e.value && !checkKvkkApproval(e.value, "kefil")) {
                                return;
                            }

                            formik.setFieldValue("guarantor", e.value);
                        }}
                        onBlur={formik.handleBlur}
                        label="Kefil"
                        showClear
                        error={formik.errors.guarantor}
                        touched={formik.touched.guarantor}
                        valueTemplate={(option, props) => {
                            if (option) {
                                return (
                                    <div className="flex items-center gap-2">
                                        <span>{option.name} {option.surname}</span>
                                        {option.kvkk && option.kvkk.id === 0 && (
                                            <i className="ml-3 pi pi-exclamation-triangle text-orange-500" title="KVKK onayı yok"></i>
                                        )}
                                    </div>
                                );
                            }
                        }}
                        itemTemplate={(option) => {
                            return (
                                <div className="flex items-center gap-2">
                                    <span>{option.name} {option.surname}</span>
                                    {option.kvkk && option.kvkk.id === 0 && (
                                        <i className="ml-3 pi pi-exclamation-triangle text-orange-500" title="KVKK onayı yok"></i>
                                    )}
                                </div>
                            );
                        }}
                        filter
                        filterBy="name,surname,identityNumber,phoneNumber1"
                    />

                    <InputText
                        name="term"
                        type="number"
                        label="Vade (Ay)"
                        value={formik.values.term}
                        onChange={(e) => {
                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        clearFunction={() => formik.setFieldValue("term", null)}
                        error={formik.errors.term}
                        touched={formik.touched.term}
                        required
                        min={1}
                        max={360}
                        step={1}
                    />

                    <InputText
                        name="amount"
                        type="number"
                        label="Tutar"
                        value={formik.values.amount}
                        onChange={(e) => {
                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        clearFunction={() => formik.setFieldValue("amount", null)}
                        error={formik.errors.amount}
                        touched={formik.touched.amount}
                        required
                        min={0}
                        currency="TRY"
                    />

                    <InputText
                        name="rate"
                        type="number"
                        label="Faiz Oranı (%)"
                        value={formik.values.rate}
                        onChange={(e) => {
                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        clearFunction={() => formik.setFieldValue("rate", "0.00")}
                        error={formik.errors.rate}
                        touched={formik.touched.rate}
                        required
                        min={0}
                        max={100}
                        step={0.01}
                        minFractionDigits={2}
                        maxFractionDigits={4}
                    />

                    <InputText
                        disabled={formik.values.paymentType === 1}
                        name="periodInput"
                        type="number"
                        label="Periyot (1-12 Ay)"
                        value={formik.values.periodInput || null}
                        onChange={(e) => {
                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        clearFunction={() => formik.setFieldValue("periodInput", null)}
                        error={formik.errors.periodInput}
                        touched={formik.touched.periodInput}
                        required
                        min={1}
                        max={12}
                        step={1}
                    />
                    <InputText
                        id="firstPaymentDate"
                        label="İlk Taksit Tarihi"
                        name="firstPaymentDate"
                        type="date"
                        value={formik.values.firstPaymentDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        clearFunction={() => formik.setFieldValue("firstPaymentDate", new Date())}
                        error={formik.errors.firstPaymentDate}
                        touched={formik.touched.firstPaymentDate}
                        required
                    />
                    <div className="md:col-span-2 xl:col-span-1">
                        <InputText
                            type="textarea"
                            label="Açıklama"
                            name="remark"
                            value={formik.values.remark}
                            onChange={(e) => {
                                formik.handleChange(e);
                            }}
                            onBlur={formik.handleBlur}
                            clearFunction={() => formik.setFieldValue("remark", "")}
                            error={formik.errors.remark}
                            touched={formik.touched.remark}
                            required
                        />
                    </div>
                </div>

                {/* Buttons - Responsive */}
                <div className="flex flex-col sm:flex-row sm:flex-wrap justify-end gap-3 sm:gap-5 mt-4 sm:mt-5">
                    {/* Word butonu - sadece desktop'ta görünür */}
                    {showPaymentTable && (
                        <Button
                            type="button"
                            className="hidden sm:flex rounded-xl shadow-lg cursor-pointer px-3 sm:px-5 py-2 dark:bg-purple-500 dark:text-white dark:border-purple-500 dark:hover:bg-purple-700 dark:hover:border-purple-700 bg-purple-50 text-purple-700 hover:text-white border border-purple-700 hover:bg-purple-600 text-xs sm:text-sm"
                            label="Çıktı Al (Word)"
                            icon="pi pi-file"
                            onClick={generateDOCX}
                        />
                    )}
                    <Button
                        type="button"
                        className="w-full sm:w-auto rounded-xl shadow-lg cursor-pointer px-3 sm:px-5 py-2 dark:bg-blue-500 dark:text-white dark:border-blue-500 dark:hover:bg-blue-700 dark:hover:border-blue-700 bg-blue-50 text-blue-700 hover:text-white border border-blue-700 hover:bg-blue-600 text-xs sm:text-sm"
                        label={formik.values.paymentType === 4 ? "Ödemeyi Düzenle" : "Ödeme Listesi Gör"}
                        icon={formik.values.paymentType === 4 ? "pi pi-pencil" : "pi pi-list"}
                        onClick={() => {
                            handlePaymentCalculation();
                        }}
                    />
                    <Button
                        type="button"
                        className="w-full sm:w-auto rounded-xl shadow-lg cursor-pointer px-3 sm:px-5 py-2 dark:bg-orange-400 dark:text-white dark:border-orange-400 dark:hover:bg-orange-600 dark:hover:border-orange-600 bg-orange-50 text-orange-600 hover:text-white border border-orange-600 hover:bg-orange-600 text-xs sm:text-sm"
                        label="Sıfırla"
                        icon="pi pi-refresh"
                        onClick={() => {
                            showDialog({
                                message: "Mevcut veriyi sıfırlamak istediğinize emin misiniz ?",
                                onConfirm: () => {
                                    formik.resetForm();
                                    // Reset sonrası default değerleri tekrar set et
                                    setTimeout(() => {
                                        formik.setFieldValue("rate", "0.00");
                                        formik.setFieldValue("firstPaymentDate", new Date());
                                    }, 0);
                                    setCalculatedPayments({});
                                    setShowPaymentTable(false);
                                    setShowDynamicPaymentDialog(false);
                                }
                            });
                        }}
                    />
                    <Button
                        type="button"
                        className="w-full sm:w-auto rounded-xl shadow-lg cursor-pointer px-3 sm:px-5 py-2 dark:bg-green-400 dark:text-white dark:border-green-400 dark:hover:bg-green-600 dark:hover:border-green-600 bg-green-50 text-green-600 hover:text-white border border-green-600 hover:bg-green-600 text-xs sm:text-sm"
                        label="Kaydet"
                        icon="pi pi-save"
                        loading={formik.isSubmitting}
                        disabled={
                            !formik.isValid ||
                            formik.isSubmitting ||
                            !formik.values.customer ||
                            !formik.values.term ||
                            !formik.values.amount ||
                            !formik.values.rate ||
                            !formik.values.firstPaymentDate ||
                            !formik.values.remark ||
                            formik.values.paymentType === 0 ||
                            ((formik.values.paymentType === 2 || formik.values.paymentType === 3) && !formik.values.periodInput)
                        }
                        onClick={() => {
                            showDialog({
                                message: "Ödeme planını kaydetmek istediğinize emin misiniz ?",
                                onConfirm: () => {
                                    formik.submitForm();
                                }
                            });
                        }}
                    />
                </div>

                {/* Dynamic Payment Dialog */}
                <DynamicPayment
                    visible={showDynamicPaymentDialog}
                    onHide={() => setShowDynamicPaymentDialog(false)}
                    data={{
                        orderAmount: parseFloat(formik.values.amount),
                        term: parseInt(formik.values.term),
                        rate: parseFloat(formik.values.rate),
                        firstPaymentDate: formik.values.firstPaymentDate // Buraya firstPaymentDate eklendi
                    }}
                    onGenerate={handleDynamicPaymentGenerate}
                />

                {/* Payment Table - Responsive */}
                {showPaymentTable && Object.entries(calculatedPayments).length > 0 && (
                    <div className="mt-4 sm:mt-6">
                        <Accordion
                            activeIndex={formik.values.paymentType === 0 ? null : 0}
                            multiple={formik.values.paymentType === 0}
                            pt={accordionPT}
                        >
                            {Object.entries(calculatedPayments).map(([paymentType, payments], index) => (
                                <AccordionTab
                                    key={paymentType}
                                    pt={{
                                        headerAction: {
                                            className: "bg-gray-50 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-800 rounded-md transition-colors duration-300 border dark:border-black shadow-lg"
                                        },
                                        content: {
                                            className: "bg-gray-50 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-800 rounded-md transition-colors duration-300 border dark:border-black shadow-lg"
                                        }
                                    }}
                                    header={
                                        <div className="flex flex-col gap-3">
                                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
                                                <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100">{paymentType}</span>
                                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                                    {`Toplam Taksit: ${payments.length}`}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded-md">
                                                <div className="text-center">
                                                    <div className="font-medium text-gray-600 dark:text-gray-400">Vade</div>
                                                    <div className="font-semibold text-gray-900 dark:text-gray-100">{formik.values.term} Ay</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-medium text-gray-600 dark:text-gray-400">Ana Para</div>
                                                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                                                        ₺{parseFloat(formik.values.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                                    </div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-medium text-gray-600 dark:text-gray-400">Faiz Oranı</div>
                                                    <div className="font-semibold text-gray-900 dark:text-gray-100">%{formik.values.rate || 0}</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-medium text-gray-600 dark:text-gray-400">Toplam Geri Ödeme</div>
                                                    <div className="font-semibold text-green-600 dark:text-green-400">
                                                        ₺{payments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                >
                                    <div className="relative overflow-hidden">
                                        <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
                                            <DataTable
                                                value={payments}
                                                className="custom-datatable"
                                                pt={dataTablePT}
                                                rowClassName={() => "bg-gray-50 hover:bg-gray-100 dark:bg-gray-600 dark:hover:bg-gray-700 text-black dark:text-white"}
                                                scrollable
                                                scrollHeight="400px"
                                                tableStyle={{ minWidth: '500px', tableLayout: 'fixed' }}
                                            >
                                                <Column
                                                    field="month"
                                                    header={formik.values.firstPaymentDate ? "Vade Tarihi" : "Ay"}
                                                    style={{ width: '15%', minWidth: '60px' }}
                                                    headerStyle={{ fontSize: '0.75rem' }}
                                                    bodyStyle={{ fontSize: '0.75rem' }}
                                                    body={(rowData) => {
                                                        if (formik.values.firstPaymentDate) {
                                                            const paymentDate = getPaymentDate(rowData.month - 1);
                                                            return formatPaymentDate(paymentDate);
                                                        }
                                                        return rowData.month;
                                                    }}
                                                />
                                                <Column
                                                    field="amount"
                                                    header="Taksit"
                                                    style={{ width: '20%', minWidth: '100px' }}
                                                    headerStyle={{ fontSize: '0.75rem' }}
                                                    bodyStyle={{ fontSize: '0.75rem' }}
                                                    body={(rowData) => `₺${parseFloat(rowData.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
                                                />
                                                <Column
                                                    field="principal"
                                                    header="Anapara"
                                                    style={{ width: '20%', minWidth: '100px' }}
                                                    headerStyle={{ fontSize: '0.75rem' }}
                                                    bodyStyle={{ fontSize: '0.75rem' }}
                                                    body={(rowData) => `₺${parseFloat(rowData.principal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
                                                />
                                                <Column
                                                    field="interest"
                                                    header="Faiz"
                                                    style={{ width: '20%', minWidth: '100px' }}
                                                    headerStyle={{ fontSize: '0.75rem' }}
                                                    bodyStyle={{ fontSize: '0.75rem' }}
                                                    body={(rowData) => `₺${parseFloat(rowData.interest).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
                                                />
                                                <Column
                                                    field="remainingPrincipal"
                                                    header="Kalan"
                                                    style={{ width: '25%', minWidth: '100px' }}
                                                    headerStyle={{ fontSize: '0.75rem' }}
                                                    bodyStyle={{ fontSize: '0.75rem' }}
                                                    body={(rowData) => `₺${parseFloat(rowData.remainingPrincipal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
                                                />
                                            </DataTable>
                                        </div>
                                    </div>
                                </AccordionTab>
                            ))}
                        </Accordion>
                    </div>
                )}
            </form>
        </div>
    );
};

export default AddPay;